import {
  AfterViewInit,
  Component,
  DestroyRef,
  Injector,
  OnInit,
  Signal,
  ViewChild,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subscription, catchError, of, take } from 'rxjs';

import {
  EntityInterface,
  FilterableColCfg,
  makeAvailableFilterFields,
  makeFilterCfgMap,
  makeTranslatedFilterCfgMap
} from '@gfe/core/models/entities/entity-visualization-interface';
import { CurrentLangService } from '@gfe/core/services/common/current-lang.service';
import { PagedEntityService } from '@gfe/core/services/entities/paged-entity.service';
import { EntityIconService } from '@gfe/core/services/entity-icon/entity-icon.service';
import { EntityLabelsTranslationService } from '@gfe/core/services/entity-labels-translation/entity-labels-translation.service';
import {
  selectLabels,
  selectTags
} from '@gfe/core/store/admin/tags-labels/tags-labels.selectors';
import { selectDataspaceState } from '@gfe/core/store/dataspace/dataspace.selectors';
import {
  selectEntityAutoCompleteGlobalData,
  selectPlan
} from '@gfe/core/store/plan/plan.selectors';
import { isNil } from '@gfe/core/utils/shared-utils';
import { FilterPresetsComponent } from '@gfe/features/components/entities/filter-presets/filter-presets.component';
import {
  AppliedFilterPreset,
  FilterPresetsStore
} from '@gfe/features/components/entities/filter-presets/filter-presets.store';
import {
  DrawerAction,
  DrawerComponent,
  DrawerConfig,
  DrawerSize
} from '@gfe/shared/components/drawer/drawer.component';
import { DropdownSingletonComponent } from '@gfe/shared/components/dropdown-singleton/dropdown-singleton.component';
import { DropdownService } from '@gfe/shared/components/dropdown-singleton/dropdown.service';
import { FilterQueryBuilderComponent } from '@gfe/shared/components/filter-query-builder/filter-query-builder.component';
import {
  AvailableFilterField,
  FilterConfiguration
} from '@gfe/shared/components/filter-query-builder/filter.models';
import { PaginationConfig } from '@gfe/shared/components/pagination/pagination.component';
import {
  QualifiersConfig,
  SearchResult
} from '@gfe/shared/components/search-query-builder/search-query-builder.component';
import { SearchResultBuilder } from '@gfe/shared/components/search-query-builder/search-result-builder';
import { SortState, TableConfig } from '@gfe/shared/components/table/table.model';
import {
  SearchConfig,
  ToolboxComponent
} from '@gfe/shared/components/toolbox/toolbox.component';
import {
  addOrReplaceConditionInConfig,
  createFilterCondition,
  findFilterField,
  removeConditionFromConfig
} from '@gfe/shared/utils/filter-condition.utils';
import {
  SortableFieldRef,
  buildFilterPresetPayload,
  buildPathFilterPresetPayload
} from '@gfe/shared/utils/filter-preset.utils';

import { FilterField } from '@core/filters/path-filter/vocabulary/vocabulary';
import { ICONS } from '@core/services/icons/icons.service';

import { PathFilterComposerComponent } from '@shared/components/path-filter/composer/path-filter-composer.component';
import { PathFilterPanelComponent } from '@shared/components/path-filter/panel/path-filter-panel.component';
import { PathFilterPillsComponent } from '@shared/components/path-filter/pills/path-filter-pills.component';
import { PathFilterSearchBarComponent } from '@shared/components/path-filter/search-bar/path-filter-search-bar.component';
import { PathFilterSortComponent } from '@shared/components/path-filter/sort/path-filter-sort.component';
import { pathOrderBy } from '@shared/components/path-filter/sort/sort-options';
import { TableComponent } from '@shared/components/table/table.component';
import { FocusFirstDirective } from '@shared/directives/focus-first.directive';
import { ReturnFocusDirective } from '@shared/directives/return-focus.directive';

import { EntityTableService } from '@features/components/entities/entity-table.service';
import { PathFilterMoveButtonsComponent } from '@features/components/entities/path-filter-bar/move-buttons/path-filter-move-buttons.component';
import { PathFilterBarState } from '@features/components/entities/path-filter-bar/path-filter-bar.state';

import {
  EntityAutoCompleteService,
  EntityPickerFrame
} from './entity-auto-complete.service';

const OPEN_FILTER_FOCUS_RESET_MS = 100;
const BASE_Z_INDEX = 200;
const Z_INDEX_PER_DEPTH = 100;

@Component({
  providers: [
    EntityTableService,
    PagedEntityService,
    TranslateService,
    DropdownService,
    FilterPresetsStore,
    PathFilterBarState
  ],
  selector: 'app-entity-auto-complete-dialog',
  templateUrl: './entity-auto-complete-dialog.component.html',
  imports: [
    TableComponent,
    ToolboxComponent,
    DropdownSingletonComponent,
    DrawerComponent,
    FilterQueryBuilderComponent,
    FilterPresetsComponent,
    TranslatePipe,

    PathFilterSearchBarComponent,
    PathFilterPillsComponent,
    PathFilterSortComponent,
    PathFilterPanelComponent,
    PathFilterComposerComponent,
    PathFilterMoveButtonsComponent,
    FocusFirstDirective,
    ReturnFocusDirective
  ],
  host: {
    '(document:keydown.escape)': 'onEscape()'
  }
})
export class EntityAutoCompleteDialogComponent<T> implements OnInit, AfterViewInit {
  frame = input.required<EntityPickerFrame>();
  depth = input<number>(0);

  // Entity type that will be autocompleted (coming from x-reference)
  entityType = computed<string>(() => this.frame().entityType);

  searchQuery = signal<string>('');
  searchCfg = this.buildSearchConfig();
  readonly entityTableService = inject(EntityTableService);
  columns = this.entityTableService.columns;

  private readonly store = inject(Store);
  private readonly pagedEntityService = inject(PagedEntityService);
  private readonly plan = this.store.selectSignal(selectPlan);
  private readonly dataspace = this.store.selectSignal(selectDataspaceState);
  private readonly sortState = signal<SortState<EntityInterface<T>>[]>([]);
  private readonly searchResult = signal<SearchResult>(SearchResultBuilder.empty());
  /**
   * Edits refetch at once, undebounced: an earlier request answering last would put its rows under
   * the newer filter, so each fetch or page change cancels the one before.
   */
  private rowsFetch: Subscription | null = null;
  private readonly isArray = computed<boolean>(() => this.frame().isArray);
  private readonly showGlobalData = this.store.selectSignal(
    selectEntityAutoCompleteGlobalData
  );
  private readonly availableLabels = this.store.selectSignal(selectLabels);
  private readonly availableTags = this.store.selectSignal(selectTags);
  // Global service: lets onEscape detect an external dropdown that owns the key
  private readonly dropDownService = inject(DropdownService, { skipSelf: true });
  // Local service: scoped to this component's subtree so table column menus use a local singleton
  private readonly localDropDownService = inject(DropdownService);
  private readonly autoCompleteService = inject(EntityAutoCompleteService);
  private readonly filterPresetsStore = inject(FilterPresetsStore);
  private readonly translateService = inject(TranslateService);
  private readonly currentLang = inject(CurrentLangService).currentLang;
  private readonly entityIcons = inject(EntityIconService);
  protected fetchedEntitiesInPage = signal<Map<string, EntityInterface<T>>>(new Map());
  protected tableData = signal<EntityInterface<T>[]>([]);
  protected selectedEntities = signal<Set<EntityInterface<T>>>(
    new Set<EntityInterface<T>>()
  );
  @ViewChild(ToolboxComponent) toolbox!: ToolboxComponent;
  @ViewChild('localDropdown') localDropdownSingleton!: DropdownSingletonComponent;

  protected readonly DrawerSize = DrawerSize;
  protected readonly ICONS = ICONS;

  private readonly injector = inject(Injector);
  protected readonly pathBar = inject(PathFilterBarState);
  protected readonly activeGrammar = this.pathBar.activeGrammar;
  protected readonly toolboxSearchConfig = this.pathBar.toolboxSearch(() =>
    this.searchCfg()
  );
  private readonly pills = viewChild(PathFilterPillsComponent);

  /** Own layer. Dropdowns raised from this picker sit half a band above it. */
  protected readonly zIndex = computed(
    () => BASE_Z_INDEX + this.depth() * Z_INDEX_PER_DEPTH
  );
  protected readonly dropdownZIndex = computed(() => this.zIndex() + Z_INDEX_PER_DEPTH / 2);

  private readonly isTopmost = computed(
    () => this.autoCompleteService.topFrameId() === this.frame().id
  );

  /** Sort a preset restored, seeded into the table so its headers agree with the data. */
  protected readonly presetSort = signal<SortState<EntityInterface<T>>[]>([]);

  protected readonly advancedFilters = signal<FilterConfiguration | null>(null);
  protected readonly showAdvancedFiltering = signal<boolean>(false);
  /**
   * Condition the filter builder should open for editing. Set to a condition id to focus
   * it, then cleared so re-filtering the same column focuses it again.
   */
  protected readonly openFilterForField = signal<string | null>(null);

  protected readonly availableAdvancedFilters = computed<AvailableFilterField[]>(() => {
    this.availableLabels();
    const tableCfg = this.entityCfg();
    if (isNil(tableCfg)) {
      return [];
    }
    return makeAvailableFilterFields(
      tableCfg,
      this.entityLabelsTranslationService,
      this.availableTags()
    );
  });

  /** Total matching entities, before the already-picked ones are filtered out. */
  protected readonly totalEntities = computed(
    () => this.pagedEntityService.paginationState().total
  );

  protected readonly drawerTitle = computed(() => {
    this.currentLang();
    return this.translateService.instant('form.entity_picker.title', {
      entity: this.entityTableService.entityDisplayName()
    });
  });

  protected readonly shownLabel = computed(() => {
    this.currentLang();
    return this.translateService.instant('form.entity_picker.shown', {
      shown: this.tableData().length,
      total: this.totalEntities()
    });
  });

  protected readonly drawerConfig = computed<DrawerConfig>(() => {
    this.currentLang();
    const actions: DrawerAction[] = [
      {
        label: this.translateService.instant('common.cancel'),
        color: 'cancel',
        callback: (): void => this.close()
      }
    ];

    // Single selection commits on row click, so only a multi-selection needs confirming.
    if (this.isArray()) {
      actions.push({
        label: this.translateService.instant('common.add'),
        color: 'primary',
        disabled: this.selectedEntities().size === 0,
        callback: (): void => this.confirmSelection()
      });
    }

    return {
      icon: this.entityIcons.getEntityIcon(this.entityType()),
      dismissible: true,
      closeOnBackdropClick: true,
      // Escape is handled here instead: it must dismiss an open column menu or filter
      // popup before it dismisses the drawer holding them.
      closeOnEscape: false,
      actions
    };
  });

  readonly entityLabelsTranslationService = inject(EntityLabelsTranslationService);
  readonly entityCfg = this.entityTableService.entityCfg;

  // Map from column access key to filter cfg
  filterableColMap = computed<Record<string, FilterableColCfg>>(() => {
    const tableCfg = this.entityCfg();
    if (isNil(tableCfg)) {
      return {};
    }
    return makeFilterCfgMap(tableCfg);
  });

  /** Sortable columns of the picked entity, so a stored sort resolves to a column key. */
  private readonly sortableFieldRefs = computed<SortableFieldRef[]>(() => {
    const labels = new Map(
      this.columns().map((column) => [String(column.key), column.label])
    );
    return Object.entries(this.entityTableService.sortableColMap()).map(
      ([columnKey, cfg]) => ({
        columnKey,
        dbColName: cfg.dbColName,
        ...(isNil(cfg.labelKey) ? {} : { labelKey: cfg.labelKey }),
        label: labels.get(columnKey) ?? columnKey
      })
    );
  });

  /** What saving a preset from this picker would store. */
  private readonly filterPresetPayload = computed(() => {
    const tableCfg = this.entityCfg();
    const sortState = this.sortState();
    const columnSort =
      sortState.length === 0 ? undefined : this.entityTableService.getOrderBy(sortState);
    // This picker keeps its header sort apart from its Path sort list; a preset stores both.
    if (this.activeGrammar() === 'path') {
      return buildPathFilterPresetPayload(
        this.pathBar.wire(),
        pathOrderBy(this.pathBar.sort(), columnSort) ?? []
      );
    }
    return buildFilterPresetPayload(
      this.searchResult(),
      this.advancedFilters(),
      isNil(tableCfg)
        ? {}
        : makeTranslatedFilterCfgMap(tableCfg, this.entityLabelsTranslationService),
      columnSort
    );
  });

  constructor() {
    inject(DestroyRef).onDestroy(() => this.rowsFetch?.unsubscribe());
    effect(() =>
      this.entityTableService.setPathFilteredFields(this.pathBar.filteredColumnNames())
    );
    effect(() => {
      this.filterPresetsStore.entityType.set(this.entityType());
      this.filterPresetsStore.presetContext.set(this.frame().presetContext);
      this.filterPresetsStore.dataSpaceId.set(this.dataspace().dataspace?.id);
      this.filterPresetsStore.availableFields.set(this.availableAdvancedFilters());
      this.filterPresetsStore.sortableFields.set(this.sortableFieldRefs());
      this.filterPresetsStore.currentPayload.set(this.filterPresetPayload());
    });

    this.filterPresetsStore.applied
      .pipe(takeUntilDestroyed())
      .subscribe((applied) => this.onFilterPresetApplied(applied));

    effect(() => this.pathBar.entityType.set(this.entityType()));
    this.pathBar.onEdit(() => this.afterPathEdit());
    this.pathBar.onBarEdit(() => this.afterBarEdit());
    this.pathBar.classicFiltered.set(() => {
      const { filters, search } = this.filterPresetPayload();
      return !isNil(filters) || !isNil(search);
    });
    // The first request waits until it is known whether this picker opens in Path.
    effect(() => {
      if (!this.pathBar.fetchHeld()) untracked(() => this.getEntities());
    });
  }

  ngOnInit(): void {
    this.entityTableService.setEntityType(this.entityType());
  }

  ngAfterViewInit(): void {
    this.localDropDownService.register(this.localDropdownSingleton);
  }

  protected tableConfig: Signal<TableConfig<EntityInterface<T>>> = computed(() => {
    return {
      bordered: false,
      editable: {
        inline: false,
        newTab: false
      },
      rowNumbers: false,
      columnTooltips: false,
      horizontalScroll: false,
      columnActionSettings: {
        sortable: true,
        filterable: true,
        groupable: false,
        movable: true,
        resizeable: true,
        hideable: true
      },
      selection: {
        enabled: true,
        multiple: this.isArray(),
        mode: this.isArray() ? 'checkbox' : 'click',
        highlightSelected: true,
        action: (row: EntityInterface<T>) => {
          if (this.isArray()) {
            if (this.selectedEntities().has(row)) {
              this.selectedEntities.update((data) => {
                const newSet = new Set(data);
                newSet.delete(row);
                return newSet;
              });
            } else {
              this.selectedEntities.update((data) => {
                const newSet = new Set(data);
                newSet.add(row);
                return newSet;
              });
            }
            return;
          }
          this.frame().values.set(row.uid);
          this.close();
        }
      }
    };
  });

  toolboxPagination = signal<PaginationConfig>({
    currentPage: 0,
    totalPages: 1
  });

  /** Dismisses this picker, and any picker opened from it. */
  close(): void {
    this.localDropDownService.close();
    this.autoCompleteService.hide(this.frame().id);
  }

  onPageChange(pageNumber: number): void {
    this.rowsFetch?.unsubscribe();
    this.rowsFetch = this.pagedEntityService
      .goToPage<T>(pageNumber)
      .pipe(
        take(1),
        catchError(() => {
          this.fetchedEntitiesInPage.set(new Map());
          return of();
        })
      )
      .subscribe((res) => {
        if (!res) {
          return;
        }
        const resultWithoutCurrentUids = res.result.filter(
          (entity) => !this.frame().currentItems.includes(entity.uid)
        );
        this.tableData.set(resultWithoutCurrentUids);
        const paginationState = this.pagedEntityService.paginationState();
        this.toolboxPagination.set({
          currentPage: paginationState.page,
          totalPages: paginationState.numPages
        });
      });
  }

  onSortChange(sortState: SortState<EntityInterface<T>>[]): void {
    this.sortState.set(sortState);
    this.getEntities();
  }

  private getEntities(): void {
    if (this.pathBar.fetchHeld()) return;
    const sortState = this.sortState();
    const entityVersionName = this.entityTableService.getEntityVersionName();
    if (isNil(entityVersionName)) {
      return;
    }

    const dataspaceId = this.dataspace().dataspace?.id;
    if (isNil(dataspaceId)) {
      return;
    }

    const planId = this.plan()?.id;
    const plan =
      isNil(planId) || this.showGlobalData()
        ? null
        : {
            id: planId,
            context: 'visualization' as const
          };

    const path = this.pathBar.fetch();
    const columnOrder = this.entityTableService.getOrderBy(sortState);
    this.rowsFetch?.unsubscribe();
    this.rowsFetch = this.pagedEntityService
      .fetchEntities<T>({
        entityType: this.entityType(),
        entityVersionName,
        dataSpaceId: dataspaceId,
        orderBy: path ? pathOrderBy(path.sort, columnOrder) : columnOrder,
        plan: plan ?? undefined,
        searchResult: path ? undefined : this.searchResult(),
        filterConfiguration: path ? undefined : (this.advancedFilters() ?? undefined),
        ...(path ? { path: { filter: path.filter, search: path.search } } : {})
      })
      .pipe(
        catchError((error: unknown) => {
          this.fetchedEntitiesInPage.set(new Map());
          this.pathBar.reportError(error, this.translateService.instant('common.error'));
          return of();
        })
      )
      .subscribe((res) => {
        this.pathBar.error.set(null);
        this.fetchedEntitiesInPage.update(() => {
          const newMap = new Map();
          res.result.forEach((entity) => {
            newMap.set(entity.uid, entity);
          });
          const resultWithoutCurrentUids = res.result.filter(
            (entity) => !this.frame().currentItems.includes(entity.uid)
          );
          this.tableData.set(resultWithoutCurrentUids);
          const paginationState = this.pagedEntityService.paginationState();
          this.toolboxPagination.set({
            currentPage: paginationState.page,
            totalPages: paginationState.numPages
          });
          return newMap;
        });
      });
  }

  /** Column-menu filter action: seeds a condition on that column and opens it for editing. */
  protected addFilterOn(column: keyof EntityInterface<T>): void {
    if (this.activeGrammar() === 'path') {
      const field = this.pathFieldFor(column as string);
      if (field) this.openPillFor(field);
      return;
    }

    const filterCfg = this.filterableColMap()[column];
    if (isNil(filterCfg)) {
      return;
    }

    const condition = createFilterCondition(
      findFilterField(this.availableAdvancedFilters(), filterCfg.dbColName)
    );
    if (isNil(condition)) {
      return;
    }

    this.onFilterChanges(addOrReplaceConditionInConfig(this.advancedFilters(), condition));

    this.openFilterForField.set(condition.id);
    setTimeout(() => {
      this.openFilterForField.set(null);
    }, OPEN_FILTER_FOCUS_RESET_MS);
  }

  protected removeFilterOn(column: keyof EntityInterface<T>): void {
    if (this.activeGrammar() === 'path') {
      const field = this.pathFieldFor(column as string);
      if (field) this.pathBar.removeConditionsOn(field);
      return;
    }

    const filterCfg = this.filterableColMap()[column];
    if (isNil(filterCfg)) return;
    this.onFilterChanges(
      removeConditionFromConfig(this.advancedFilters(), filterCfg.dbColName)
    );
  }

  /** Applies an edit made in the filter builder, or seeded from a column menu. */
  protected onFilterChanges(config: FilterConfiguration): void {
    this.advancedFilters.set(config);
    // Column headers read their filtered state from the table service, not from here.
    this.entityTableService.setFilterConfiguration(config);
    this.pagedEntityService.resetPagination();
    this.selectedEntities.set(new Set());
    this.getEntities();
  }

  /** The same consequences as a classic filter edit: the preset no longer describes it, page one, no ticks. */
  private afterPathEdit(): void {
    this.filterPresetsStore.detach();
    this.afterBarEdit();
  }

  /** The bar is outside presets, so editing it keeps the active one. */
  private afterBarEdit(): void {
    this.pagedEntityService.resetPagination();
    this.selectedEntities.set(new Set());
    this.getEntities();
  }

  /** Commits a multi-selection to the field that opened the picker. */
  protected confirmSelection(): void {
    const uids = [...this.selectedEntities()].map((entity) => entity.uid);
    this.frame().values.set(uids);
    this.close();
  }

  /**
   * Escape closes the picker only when it is the topmost one and nothing it contains claims
   * the key first — a column menu or filter popup has to be dismissed on its own before the
   * drawer goes.
   */
  protected onEscape(): void {
    if (
      this.isTopmost() &&
      !this.dropDownService.isOpen() &&
      !this.localDropDownService.isOpen()
    ) {
      this.close();
    }
  }

  /**
   * Pushes a preset into the picker. Dropped fields are not reported here the way the
   * entity list reports them: the picker is a transient overlay, and a notification about
   * a preset the user only borrowed to narrow a list would outlive it.
   */
  private onFilterPresetApplied(applied: AppliedFilterPreset): void {
    this.pathBar.applyPreset(applied, {
      apply: (preset) => this.applyPresetToPicker(preset),
      refuse: () => this.filterPresetsStore.detach()
    });
  }

  /** The picker's share of a preset: the bar already holds its Path part. */
  private applyPresetToPicker(applied: AppliedFilterPreset): void {
    const isPath = applied.grammar === 'path';
    const filters = isPath ? null : (applied.filters ?? null);
    this.advancedFilters.set(filters);
    this.entityTableService.setFilterConfiguration(filters);

    const search = isPath ? '' : (applied.search ?? '');
    this.searchQuery.set(search);
    const searchResult =
      search.length === 0
        ? SearchResultBuilder.empty()
        : {
            ...SearchResultBuilder.empty(),
            rawQuery: search,
            textTerms: search.split(/\s+/).filter((term) => term.length > 0)
          };
    this.searchResult.set(searchResult);
    this.entityTableService.setSearchResult(search.length === 0 ? null : searchResult);

    // A preset without a stored sort leaves the current ordering as it is.
    if (!isNil(applied.sort)) {
      const sort = applied.sort as SortState<EntityInterface<T>>[];
      this.presetSort.set(sort);
      this.sortState.set(sort);
    }

    if (!isPath && !isNil(applied.filters)) {
      this.showAdvancedFiltering.set(true);
    }

    this.pagedEntityService.resetPagination();
    this.selectedEntities.set(new Set());
    this.getEntities();
  }

  protected onAdvancedQuerySearch($event: SearchResult) {
    this.searchResult.set($event);
    this.entityTableService.setSearchResult($event);
    this.pagedEntityService.resetPagination();
    this.selectedEntities.set(new Set());
    this.getEntities();
  }

  protected onListItemClick(event: {
    row: EntityInterface<T>;
    column: string;
    item: unknown;
  }): void {
    if (this.activeGrammar() === 'path') {
      this.addPathListItem(event.column, String(event.item));
      return;
    }

    const filterCfg = this.filterableColMap()[event.column];
    if (isNil(filterCfg)) {
      return;
    }

    const translatedLabel = this.entityLabelsTranslationService.translateSearchFilterLabel(
      filterCfg.translationLabel
    );

    // Quote the value if it contains spaces
    const itemValue = String(event.item);
    const quotedValue = itemValue.includes(' ') ? `"${itemValue}"` : itemValue;
    const filterString = `${translatedLabel}:${quotedValue}`;

    const currentQuery = this.searchQuery();
    const newQuery = currentQuery ? `${currentQuery} ${filterString}` : filterString;
    this.toolbox.updateSearchQuery(newQuery);
  }

  /** The pills row sits behind the funnel, so it is shown before the field's popover opens. */
  private openPillFor(field: FilterField): void {
    this.showAdvancedFiltering.set(true);
    afterNextRender(() => this.pills()?.openField(field), { injector: this.injector });
  }
  /**
   * A column without a Classic filter config (a reference list such as compatible trailers) still
   * names its field by its key, which Path can filter on.
   */
  private pathFieldFor(column: string): FilterField | undefined {
    const dbColName =
      this.filterableColMap()[column]?.dbColName ?? column.replace(/^raw\./, '');
    return this.pathBar.fieldForColumn(dbColName);
  }

  private addPathListItem(column: string, item: string): void {
    const field = this.pathFieldFor(column);
    if (!field) return;
    if (!this.pathBar.addListItem(field, item)) this.openPillFor(field);
  }

  private buildSearchConfig(): Signal<SearchConfig> {
    return computed<SearchConfig>(() => ({
      active: true,
      type: 'advanced',
      shortcut: true,
      qualifiers: this.columns().reduce((acc, col) => {
        const filterCfg = this.filterableColMap()[col.key];
        if (!isNil(filterCfg)) {
          const translatedLabel =
            this.entityLabelsTranslationService.translateSearchFilterLabel(
              filterCfg.translationLabel
            );

          acc[translatedLabel] = {
            type: 'text', // Need to be enum if we know the possibles values
            description: '',
            supportsRegex: true
          };
        }
        return acc;
      }, {} as QualifiersConfig)
    }));
  }
}
